import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  CreationOptions,
  createSyncGroup,
  getGroupCreationOptions,
  linkageLabels,
  LinkageField,
} from "../../api/syncConfiguration";
import { toReadableText } from "../utils/utils";

function errorMessage(error: any) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.sync_group_id)
    return `${detail.message} (sync group #${detail.sync_group_id}).`;
  if (Array.isArray(detail))
    return detail
      .map((item) => `${item.loc?.slice(1).join(" ")}: ${item.msg}`)
      .join("; ");
  return "Unable to save the sync group. Check your connection and retry.";
}
export function AddSyncGroupDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [options, setOptions] = useState<CreationOptions>();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [first, setFirst] = useState<number | "">("");
  const [second, setSecond] = useState<number | "">("");
  const [attributes, setAttributes] = useState<
    Partial<Record<LinkageField, number>>
  >({});
  const [searches, setSearches] = useState("100");
  const [refreshes, setRefreshes] = useState("100");
  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await getGroupCreationOptions();
      setOptions(data);
      setSearches(String(data.defaults.max_reference_data_searches));
      setRefreshes(String(data.defaults.max_reference_data_refreshes));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (open) {
      setFirst("");
      setSecond("");
      setAttributes({});
      setError("");
      setOptions(undefined);
      void load();
    }
  }, [open]);
  const pair = options?.pairs.find(
    (p) =>
      (p.system_a_id === first && p.system_b_id === second) ||
      (p.system_b_id === first && p.system_a_id === second)
  );
  const systemLabel = (id: number) => {
    const s = options.systems.find((s) => s.id === id);
    return `${s?.name} (${toReadableText(s?.system_type || "")}) · #${id}`;
  };
  const compatible = (id: number) =>
    options?.pairs.some(
      (p) =>
        (p.system_a_id === first && p.system_b_id === id) ||
        (p.system_b_id === first && p.system_a_id === id)
    );
  const limitValid = (value: string) =>
    /^\d+$/.test(value) && Number(value) <= 2147483647;
  const canSave =
    pair &&
    pair.required_attribute_fields.every((field) => attributes[field]) &&
    (!pair.reference_limits || (limitValid(searches) && limitValid(refreshes)));
  const save = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError("");
    try {
      await createSyncGroup({
        system_a_id: pair.system_a_id,
        system_b_id: pair.system_b_id,
        ...attributes,
        ...(pair.reference_limits
          ? {
              max_reference_data_searches: Number(searches),
              max_reference_data_refreshes: Number(refreshes),
            }
          : {}),
      });
      onCreated();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!saving) onClose();
      }}
      fullWidth
      maxWidth="sm"
      aria-labelledby="add-sync-group-title"
    >
      <DialogTitle id="add-sync-group-title">Add sync group</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {loading && (
            <>
              <CircularProgress size={24} />
              <Typography>Loading compatible systems…</Typography>
            </>
          )}
          {loadError && (
            <Alert
              severity="error"
              action={<Button onClick={load}>Retry</Button>}
            >
              Unable to load sync group options.
            </Alert>
          )}
          {!loading &&
            !loadError &&
            options &&
            (options.pairs.length === 0 ? (
              <Alert severity="info">
                No compatible systems are ready. Save the required source
                configurations first.
              </Alert>
            ) : (
              <>
                <Typography variant="body2">
                  Choose existing systems. This uses saved source configuration;
                  apply any source changes separately.
                </Typography>
                <TextField
                  select
                  fullWidth
                  label="First system"
                  value={first}
                  disabled={saving}
                  onChange={(e) => {
                    setFirst(Number(e.target.value));
                    setSecond("");
                    setAttributes({});
                    setError("");
                  }}
                >
                  {options.systems
                    .filter((s) =>
                      options.pairs.some(
                        (p) => p.system_a_id === s.id || p.system_b_id === s.id
                      )
                    )
                    .map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {systemLabel(s.id)}
                      </MenuItem>
                    ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  label="Second system"
                  value={second}
                  disabled={saving || first === ""}
                  onChange={(e) => {
                    setSecond(Number(e.target.value));
                    setAttributes({});
                    setError("");
                  }}
                >
                  {options.systems
                    .filter((s) => compatible(s.id))
                    .map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {systemLabel(s.id)}
                      </MenuItem>
                    ))}
                </TextField>
                {pair && (
                  <>
                    <Typography variant="body2">
                      System A: {systemLabel(pair.system_a_id)}
                      <br />
                      System B: {systemLabel(pair.system_b_id)}
                    </Typography>
                    {pair.attribute_fields.map((field) => {
                      const systemId = field.startsWith("system_a_")
                        ? pair.system_a_id
                        : pair.system_b_id;
                      const entity = field.includes("account")
                        ? "ACCOUNT"
                        : "CONTACT";
                      const eligible = options.attributes.filter(
                        (a) => a.system_id === systemId && a.entity === entity
                      );
                      return (
                        <TextField
                          key={field}
                          select
                          fullWidth
                          required={pair.required_attribute_fields.includes(
                            field
                          )}
                          label={linkageLabels[field]}
                          value={attributes[field] || ""}
                          disabled={saving}
                          helperText={
                            eligible.length
                              ? "Select a saved linkage attribute."
                              : "No eligible saved attributes. Configure the required source attribute first."
                          }
                          onChange={(e) =>
                            setAttributes({
                              ...attributes,
                              [field]: Number(e.target.value),
                            })
                          }
                        >
                          {eligible.map((a) => (
                            <MenuItem key={a.id} value={a.id}>
                              {a.label} (#{a.id})
                            </MenuItem>
                          ))}
                        </TextField>
                      );
                    })}
                    {pair.reference_limits && (
                      <>
                        <TextField
                          label="Reference-data search limit"
                          type="number"
                          value={searches}
                          onChange={(e) => setSearches(e.target.value)}
                          disabled={saving}
                          inputProps={{ min: 0, max: 2147483647, step: 1 }}
                          error={!limitValid(searches)}
                          helperText="Whole number; 0 disables searches."
                        />
                        <TextField
                          label="Reference-data refresh limit"
                          type="number"
                          value={refreshes}
                          onChange={(e) => setRefreshes(e.target.value)}
                          disabled={saving}
                          inputProps={{ min: 0, max: 2147483647, step: 1 }}
                          error={!limitValid(refreshes)}
                          helperText="Whole number; 0 disables refreshes."
                        />
                      </>
                    )}
                  </>
                )}
              </>
            ))}
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="info">
            This group becomes eligible for sync after saving. Data project
            restrictions are configured separately; without them, this group has
            no data project filter.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!canSave || saving || loading || loadError}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
